// src/components/Navbar.jsx
import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import { FiShoppingBag, FiMenu, FiX } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

// Routes stay in Albanian in all languages — a URL is an address, and
// swapping it per language would break shared links and bookmarks.
// Entries with `hash` are sections on the homepage, not separate pages.
const LINKS = [
  { to: "/", key: "nav.home", end: true },
  { to: "/produktet", key: "nav.products" },
  { hash: "trajnime", key: "nav.trainings" },
  { hash: "sherbime", key: "nav.services" },
  { to: "/kontakt", key: "nav.contact" },
];

/* Scroll to a homepage section, leaving room for the fixed bar. */
const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
};

export default function Navbar() {
  const { count, openCart } = useCart();
  const { t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", menuOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [menuOpen]);

  /* Section links: on the homepage just scroll; elsewhere go home and let
     the homepage scroll to the hash once it has rendered. */
  const goToSection = (e, hash) => {
    e.preventDefault();
    const wasOpen = menuOpen;
    setMenuOpen(false);

    if (pathname === "/") {
      // wait for the mobile menu to release the body scroll lock
      window.setTimeout(() => scrollToSection(hash), wasOpen ? 60 : 0);
    } else {
      navigate(`/#${hash}`);
    }
  };

  const renderLink = (l, Comp, extra = {}) =>
    l.hash ? (
      <Comp
        as="a"
        href={`/#${l.hash}`}
        onClick={(e) => goToSection(e, l.hash)}
        {...extra}
      >
        {t(l.key)}
      </Comp>
    ) : (
      <Comp
        as={NavLink}
        to={l.to}
        end={l.end}
        onClick={() => setMenuOpen(false)}
        {...extra}
      >
        {t(l.key)}
      </Comp>
    );

  return (
    <>
      <Bar $scrolled={scrolled || menuOpen}>
        <Inner>
          <Brand to="/" onClick={() => setMenuOpen(false)}>
            <BrandName>Kaida</BrandName>
            <BrandSub>Beauty Studio</BrandSub>
          </Brand>

          <Links>
            {LINKS.map((l) => (
              <li key={l.key}>{renderLink(l, StyledLink)}</li>
            ))}
          </Links>

          <Actions>
            <CartButton onClick={openCart} aria-label={t("nav.cart")}>
              <FiShoppingBag />
              {count > 0 && <Badge>{count}</Badge>}
            </CartButton>

            <SwitcherWrap>
              <LanguageSwitcher $text />
            </SwitcherWrap>

            <Burger
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <FiX /> : <FiMenu />}
            </Burger>
          </Actions>
        </Inner>
      </Bar>

      <MobileMenu $open={menuOpen}>
        <ul>
          {LINKS.map((l) => (
            <li key={l.key}>{renderLink(l, MobileLink)}</li>
          ))}
        </ul>
        <MenuFoot>
          <LanguageSwitcher $text />
          <span>{t("nav.tagline")}</span>
        </MenuFoot>
      </MobileMenu>
    </>
  );
}

/* ------------------------------------------------------------------ */

const Bar = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 100;
  height: ${({ theme }) => theme.layout.navHeight};
  display: flex;
  align-items: center;
  transition:
    background 0.35s ${({ theme }) => theme.ease.out},
    box-shadow 0.35s ${({ theme }) => theme.ease.out};

  ${({ $scrolled, theme }) =>
    $scrolled
      ? css`
          background: rgba(250, 248, 246, 0.9);
          backdrop-filter: saturate(160%) blur(14px);
          box-shadow: 0 1px 0 ${theme.color.lineWarm};
        `
      : css`
          background: transparent;
        `}
`;

const Inner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.layout.gutter};
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 24px;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: 1fr auto;
  }
`;

const Brand = styled(Link)`
  justify-self: start;
  display: flex;
  flex-direction: column;
  line-height: 1;
`;

const BrandName = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-size: 1.55rem;
  font-weight: ${({ theme }) => theme.weight.semi};
  color: ${({ theme }) => theme.color.ink};
  letter-spacing: -0.03em;
`;

const BrandSub = styled.span`
  font-size: 0.58rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.brand};
  margin-top: 5px;
`;

const Links = styled.ul`
  display: flex;
  align-items: center;
  gap: clamp(22px, 2.6vw, 40px);

  ${({ theme }) => theme.down.lg} {
    display: none;
  }
`;

const StyledLink = styled.a`
  position: relative;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.regular};
  color: ${({ theme }) => theme.color.inkSoft};
  padding: 6px 0;
  transition: color 0.25s;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    height: 1.5px;
    width: 100%;
    background: ${({ theme }) => theme.color.brand};
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.35s ${({ theme }) => theme.ease.out};
  }

  &:hover,
  &.active {
    color: ${({ theme }) => theme.color.brand};
  }
  &:hover::after,
  &.active::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const SwitcherWrap = styled.div`
  display: flex;
  align-items: center;
  margin-left: 6px;
  padding-left: 14px;
  border-left: 1px solid ${({ theme }) => theme.color.lineWarm};

  ${({ theme }) => theme.down.lg} {
    display: none;
  }
`;

const Actions = styled.div`
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme }) => theme.color.ink};
  font-size: 1.15rem;
  transition: background 0.25s;

  &:hover {
    background: ${({ theme }) => theme.color.bgBlush};
  }
`;

const CartButton = styled(IconButton)`
  position: relative;
`;

const Badge = styled.span`
  position: absolute;
  top: 4px;
  right: 2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.brand};
  color: #fff;
  font-size: 0.66rem;
  font-weight: ${({ theme }) => theme.weight.bold};
  line-height: 1;
`;

const Burger = styled(IconButton)`
  ${({ theme }) => theme.up.lg} {
    display: none;
  }
`;

const MobileMenu = styled.nav`
  position: fixed;
  inset: ${({ theme }) => theme.layout.navHeight} 0 0 0;
  z-index: 99;
  background: ${({ theme }) => theme.color.bgCream};
  padding: 40px ${({ theme }) => theme.layout.gutter};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  transition:
    opacity 0.3s ${({ theme }) => theme.ease.out},
    transform 0.35s ${({ theme }) => theme.ease.out},
    visibility 0.35s;

  ${({ $open }) =>
    $open
      ? css`
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        `
      : css`
          opacity: 0;
          visibility: hidden;
          transform: translateY(-12px);
        `}

  ${({ theme }) => theme.up.lg} {
    display: none;
  }
`;

const MobileLink = styled.a`
  display: block;
  padding: 16px 0;
  font-family: ${({ theme }) => theme.font.heading};
  font-size: 2rem;
  color: ${({ theme }) => theme.color.ink};
  border-bottom: 1px solid ${({ theme }) => theme.color.lineWarm};

  &.active {
    color: ${({ theme }) => theme.color.brand};
  }
`;

const MenuFoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;

  span {
    font-size: ${({ theme }) => theme.size.xs};
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.color.muted};
  }
`;
